// goalLink.js — a weekly workout-count goal takes its target from the user's
// Gym / Workout habit (2026-10-15). One source of truth: editing the habit's
// target changes the goal's displayed title and target; the goal's own
// period_target is only the fallback for a user with no gym habit.
//
// Used by both sides: src/Scorify.jsx (card title, resolver target, the edit
// form's "Set in your Gym habit" state) and api/lib/coach-run.js
// fetchUserBundle (so every server consumer — resolver, goal lines, signals,
// accountability, Kai's context — sees the linked goal). Pure ESM, no deps.

/** A weekly COUNT workout goal (not the weekly-distance shape). */
export function isWorkoutCountGoal(g) {
  if (!g || g.source_type !== 'workout') return false;
  if ((g.period || 'week') !== 'week') return false;
  const metric = g.source_config && typeof g.source_config === 'object' ? g.source_config.metric : null;
  // Any metric (weekly_distance, weekly_steps) is a measured-amount goal, not a count.
  return !metric;
}

/**
 * The gym habit's saved target, from either the client habit shape
 * ({ id: 'gym', target }) or a habits row ({ name: 'gym', target }). Null when
 * there is no gym habit or it carries no usable target.
 */
export function gymHabitTarget(habits) {
  const h = (Array.isArray(habits) ? habits : []).find(x => x && (x.id === 'gym' || x.name === 'gym') && x.active !== false);
  if (!h) return null;
  const t = Number(h.target);
  return Number.isFinite(t) && t > 0 ? t : null;
}

/** The target a workout-count goal should use: the gym habit's, else the goal's own. */
export function workoutGoalTarget(g, habits) {
  const linked = gymHabitTarget(habits);
  if (linked != null) return linked;
  const own = Number(g?.period_target ?? g?.target_value);
  return Number.isFinite(own) ? own : 0;
}

// The generated title shape for a weekly goal is `${base}: ${n} ${unit}/wk`
// (genGoalTitle in src/Scorify.jsx); with no unit that reads "Workouts a
// week: 4 /wk". A stored title in that shape is regenerated with the linked
// target; any other title is the user's own and is left alone.
const GENERATED_WEEK_TITLE = /^(.*): \d+(?:\.\d+)? \/wk$/;
export function workoutGoalTitle(g, target) {
  const title = String(g?.title || '');
  const m = title.match(GENERATED_WEEK_TITLE);
  if (!m) return title;
  return `${m[1]}: ${Number(target)} /wk`;
}

/**
 * The goal as it should be shown and resolved. For a workout-count goal with
 * a gym habit: title and targets follow the habit, and `linked_habit: 'gym'`
 * marks it so the edit form can say "Set in your Gym habit". Any other goal
 * is returned unchanged (same object).
 */
export function linkWorkoutGoal(g, habits) {
  if (!isWorkoutCountGoal(g)) return g;
  const linked = gymHabitTarget(habits);
  if (linked == null) return g;
  return { ...g, title: workoutGoalTitle(g, linked), period_target: linked, target_value: linked, linked_habit: 'gym' };
}

export function linkWorkoutGoals(goals, habits) {
  return (Array.isArray(goals) ? goals : []).map(g => linkWorkoutGoal(g, habits));
}
